import { createHttpClient } from '@src/api/core';
import { getApiConfig } from '@src/config/api';
import { isFeatureEnabled } from '@src/config/featureFlags';
import { RouterPath } from '@src/constants/router_path';
import { TIMING } from '@src/constants/timing';
import {
  PDF_GENERATION_CONFIGS,
  type PdfDocumentType,
  type PdfGenerationParamsMap,
} from '@src/modules/pdf/pdfGenerationConfigs';
import { usePdfGenerationBase } from '@src/modules/react_hooks/usePdfGenerationBase';
import { ensureValidToken, getAccessToken, isTokenValid } from '@src/modules/security/authHelpers';
import type { RootState } from '@src/store/index';
import type { PdfGenerateApiResponse } from '@src/types/shared/api/pdf_api_response';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type HttpErrorResponse = {
  status?: number;
  message?: string;
  statusText?: string;
  body?: unknown;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function usePdfGenerationByType<T extends PdfDocumentType>(documentType: T) {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const isAuthEnabled = isFeatureEnabled('AUTH_ENABLED');
  const config = PDF_GENERATION_CONFIGS[documentType];

  const mutationFn = useCallback(
    async (params: PdfGenerationParamsMap[T]): Promise<{ token: string }> => {
      const apiConfig = getApiConfig();

      if (apiConfig.isMockMode) {
        await delay(TIMING.MOCK_RESPONSE_DEFAULT_DELAY);
        return { token: config.mockToken };
      }

      const request = config.buildRequest(params);

      let accessToken: string | null = null;
      if (isAuthEnabled && auth.isAuthenticated) {
        accessToken = await ensureValidToken();
        if (!accessToken) {
          throw new Error('認証トークンの取得に失敗しました');
        }
      } else {
        accessToken = getAccessToken();
        if (accessToken && !isTokenValid()) {
          accessToken = null;
        }
      }

      const executePdfGeneration = async (token?: string | null): Promise<{ token: string }> => {
        const httpClient = createHttpClient({
          baseUrl: apiConfig.baseUrl,
          ...(token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {}),
        });

        const response = await httpClient.post(config.endpoint, request);
        return config.parseResponse(response as PdfGenerateApiResponse);
      };

      const maxRetries = 3;
      let lastError: HttpErrorResponse | null = null;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          if (!accessToken && isAuthEnabled) {
            throw new Error('アクセストークンが見つかりません');
          }

          const responseData = await executePdfGeneration(accessToken);
          return { token: responseData.token };
        } catch (error: unknown) {
          lastError = error as HttpErrorResponse;

          if (
            (error as HttpErrorResponse)?.status === 401 &&
            isAuthEnabled &&
            auth.isAuthenticated &&
            attempt < maxRetries - 1
          ) {
            try {
              if (config.retryDelayMs) {
                await delay(config.retryDelayMs);
              }
              const newAccessToken = await ensureValidToken();
              if (!newAccessToken) {
                if (typeof window !== 'undefined') {
                  window.location.href = RouterPath.login;
                }
                throw new Error('認証トークンの再取得に失敗しました');
              }
              accessToken = newAccessToken;
              continue;
            } catch (refreshError) {
              if (typeof window !== 'undefined') {
                window.location.href = RouterPath.login;
              }
              throw refreshError;
            }
          }

          if ((error as HttpErrorResponse)?.status !== 401 || attempt === maxRetries - 1) {
            break;
          }
        }
      }

      if (lastError?.status === 401 && isAuthEnabled && auth.isAuthenticated) {
        if (typeof window !== 'undefined') {
          window.location.href = RouterPath.login;
        }
      }

      const errorMessage = `${config.label}PDF生成に失敗しました（${maxRetries}回試行）: ${
        lastError?.message || lastError?.statusText || 'Unknown error'
      }`;

      console.error(`[${config.label}PDF生成エラー]`, {
        message: errorMessage,
        status: lastError?.status,
        statusText: lastError?.statusText,
        body: lastError?.body,
        retryCount: maxRetries,
      });

      config.logError?.(new Error(errorMessage), {
        status: lastError?.status,
        statusText: lastError?.statusText,
        body: lastError?.body,
        retryCount: maxRetries,
      });

      throw new Error(errorMessage);
    },
    [auth.isAuthenticated, config, isAuthEnabled]
  );

  const onSuccessCallback = useCallback(
    (data: { token: string }) => {
      dispatch(config.onSuccessAction(data.token));
    },
    [config, dispatch]
  );

  const result = usePdfGenerationBase({
    mutationFn,
    onSuccessCallback,
    queryKey: config.queryKey,
    errorQueryKey: config.errorQueryKey,
  });

  return {
    ...result,
    generatePdfDebounced: result.generateDebounced,
    generatePdfImmediate: result.generateImmediate,
    generatePdfAsync: result.generateAsync,
  };
}

export const usePdfGeneration = () => usePdfGenerationByType('resume');
